import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { XIcon } from 'lucide-react'
import {
	Badge,
	Button,
	ConfirmDialog,
	Dialog, DialogContent, DialogClose, DialogTitle,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
} from '@rentflowdev/ui'
import { INVOICE_STATUS_KEYS, INVOICE_STATUS_VARIANTS, DEFAULT_LOCALE } from '@/types'
import type { Invoice } from '@/types'
import { useGetCompanyProfileQuery, useGetInvoiceQuery, useUpdateInvoiceMutation, useDownloadInvoicePdfMutation } from '@/api'
import { EditInvoiceDialog } from '@/components/edit-invoice-dialog'
import { BankBlock } from './parts/bank-block'
import { PartiesBlock } from './parts/parties-block'
import { ItemsBlock } from './parts/items-block'
import { TotalsBlock } from './parts/totals-block'
import { SignaturesBlock } from './parts/signatures-block'
import { PaginationBar } from './parts/pagination-bar'
import { SendActions } from './parts/send-actions'
import { HistoryList, type HistoryEvent } from './parts/history-list'

interface InvoiceDetailsDialogProps {
	open:         boolean
	onOpenChange: (open: boolean) => void
	invoice:      Invoice | null
}

function formatLongDate(iso: string): string {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' })
}

export function InvoiceDetailsDialog({ open, onOpenChange, invoice }: InvoiceDetailsDialogProps) {
	const { t } = useTranslation()
	const { data: company } = useGetCompanyProfileQuery()
	// Pull a fuller version of the invoice (with printable fields) when the
	// list row only carries summary data. Falls back to the row data while loading.
	const { data: fullInvoice } = useGetInvoiceQuery(invoice?.id ?? '', { skip: !invoice?.id })
	const [updateInvoice] = useUpdateInvoiceMutation()
	const [downloadPdf, { isLoading: isDownloading }] = useDownloadInvoicePdfMutation()
	const [zoom, setZoom] = useState(120)
	const [editOpen, setEditOpen] = useState(false)
	const [cancelOpen, setCancelOpen] = useState(false)

	if (!invoice) return null
	const detail = fullInvoice ?? invoice

	const events: HistoryEvent[] = [
		detail.paidAt    && { kind: 'paid'      as const, at: detail.paidAt    },
		detail.dueDate   && { kind: 'sent'      as const, at: detail.dueDate   },
		detail.createdAt && { kind: 'generated' as const, at: detail.createdAt },
	].filter(Boolean) as HistoryEvent[]

	const invoiceNumber  = detail.invoiceNumber   ?? detail.id.slice(-4)
	const invoiceDateIso = detail.invoiceIssueDate ?? detail.createdAt
	const canEdit   = detail.status !== 'PAID' && detail.status !== 'CANCELLED'
	const canCancel = canEdit

	const handleDownloadPdf = async (format: 'A4' | 'Letter') => {
		try {
			const blob = await downloadPdf({ id: detail.id, format }).unwrap()
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = `invoice-${detail.id.slice(-8)}.pdf`
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			URL.revokeObjectURL(url)
		} catch (err) {
			console.error('Download PDF failed:', err)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col gap-0 overflow-hidden p-0 rounded-(--radius-lg)"
				style={{ width: 1112, maxWidth: '95vw', maxHeight: '92vh' }}
			>
				{/* Header bar */}
				<div className="flex items-start justify-between gap-4 border-b border-(--color-border) px-6 pt-6 pb-5">
					<div className="flex flex-col gap-1">
						<DialogTitle className="flex items-center gap-3 text-xl font-[500] leading-7 text-(--color-foreground)">
							<span>{detail.title}</span>
							<Badge variant={INVOICE_STATUS_VARIANTS[detail.status]} size="sm">
								{t(INVOICE_STATUS_KEYS[detail.status])}
							</Badge>
						</DialogTitle>
						<p className="text-sm text-(--color-muted-foreground)">
							{t('rent.invoices.details.dateFrom', { date: formatLongDate(invoiceDateIso) })}
						</p>
					</div>
					<div className="flex items-center gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger
								render={
									<Button type="button" variant="secondary" size="sm" disabled={isDownloading}>
										{t('rent.invoices.details.downloadPdf')}
									</Button>
								}
							/>
							<DropdownMenuContent align="end" className="min-w-40">
								<DropdownMenuItem onClick={() => handleDownloadPdf('A4')}>
									{t('rent.invoices.details.downloadPdfA4')}
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleDownloadPdf('Letter')}>
									{t('rent.invoices.details.downloadPdfLetter')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
						<DialogClose render={<IconButton variant="ghost" size="sm" aria-label="Close" />}>
							<XIcon className="size-5" />
						</DialogClose>
					</div>
				</div>

				<div className="flex flex-1 overflow-hidden">
					{/* Left — printable preview */}
					<div className="flex flex-1 flex-col overflow-hidden">
						<div className="flex-1 overflow-auto bg-(--color-muted)/30 p-6">
							<div className="mx-auto flex w-full max-w-[680px] flex-col gap-5 rounded-(--radius-md) border border-(--color-border) bg-(--color-card) p-6 text-(--color-foreground)">
								<BankBlock company={company} />
								<h3 className="text-base font-[600]">
									{t('rent.invoices.details.invoiceHeader', { id: invoiceNumber, date: formatLongDate(invoiceDateIso) })}
								</h3>
								<PartiesBlock invoice={detail} company={company} />
								<ItemsBlock invoice={detail} />
								<TotalsBlock invoice={detail} />
								<SignaturesBlock invoice={detail} company={company} />
							</div>
						</div>
						<PaginationBar
							page={1}
							totalPages={1}
							zoom={zoom}
							onZoomIn={() => setZoom((z) => Math.min(200, z + 10))}
							onZoomOut={() => setZoom((z) => Math.max(50, z - 10))}
						/>
					</div>

					{/* Right — sidebar */}
					<aside className="flex w-[320px] shrink-0 flex-col justify-between overflow-auto border-l border-(--color-border) bg-(--color-card) px-5 py-6">
						<div className="flex flex-col gap-5">
							<SendActions />
							<HistoryList events={events} />
						</div>
						<div className="flex flex-col gap-2 pt-6">
							<Button
								type="button"
								variant="default"
								size="full"
								disabled={!canEdit}
								onClick={() => setEditOpen(true)}
							>
								{t('rent.invoices.details.actions.correct')}
							</Button>
							<Button
								type="button"
								variant="destructive"
								size="full"
								disabled={!canCancel}
								onClick={() => setCancelOpen(true)}
							>
								{t('rent.invoices.details.actions.cancel')}
							</Button>
						</div>
					</aside>
				</div>
			</DialogContent>

			<EditInvoiceDialog
				open={editOpen}
				onOpenChange={setEditOpen}
				invoice={detail}
			/>

			<ConfirmDialog
				open={cancelOpen}
				onOpenChange={setCancelOpen}
				title={t('rent.invoices.cancelDialog.title')}
				description={t('rent.invoices.cancelDialog.description')}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('rent.invoices.details.actions.cancel')}
				variant="destructive"
				onConfirm={async () => {
					try {
						await updateInvoice({ id: detail.id, body: { status: 'CANCELLED' } }).unwrap()
						setCancelOpen(false)
					} catch (err) {
						console.error('cancelInvoice failed:', err)
					}
				}}
			/>
		</Dialog>
	)
}
